/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 *
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */
import {
  Dispatch,
  SetStateAction,
  createContext,
  useContext,
  useState,
} from "react";

import useEventRouting from "hooks/useEventRouting";
import useStateRef from "react-usestateref";

import useFetcher from "hooks/useFetcher";
import { getTrips } from "lib/tripsApi";
import { GetTripsResponse } from "lib/tripsApiTypes";
import { getProfile } from "lib/userApi";
import { GetProfileResponse } from "lib/userApiTypes";
import ROUTES from "routes";

import { useGlobal } from "./GeneralContext";

interface IUserContext {
  isLoading: boolean;
  setLoading: (isLoading: boolean) => void;
  userData: GetProfileResponse | null;
  setUserData: Dispatch<SetStateAction<GetProfileResponse | null>>;
  isGuest: boolean;
  isFormCompleted: boolean;
  setFormCompleted: (isFormCompleted: boolean) => void;
  dashboardData: GetTripsResponse;
  setDashboardData: (
    payload:
      | GetTripsResponse
      | ((data: GetTripsResponse | null) => GetTripsResponse),
  ) => void;
  getUserProfile: (
    id: string | number,
    role: "user" | "companion",
    force?: boolean,
  ) => any;
  updateProfile: (id: string | number, data: any) => void;
  reFetchDashboardData: () => void;
  isLoadingDashboardData: boolean;
}

const initialData: IUserContext = {
  isLoading: false,
  setLoading: () => {},
  userData: null,
  setUserData: () => {},
  isGuest: false,
  isFormCompleted: false,
  setFormCompleted: () => {},
  dashboardData: {
    general_flights: [],
    general_reservations: [],
    general_sessions: {},
    approved_trips: [],
    pending_trips: [],
    activities: {},
  },
  setDashboardData: () => {},
  getUserProfile: () => {},
  updateProfile: () => {},
  reFetchDashboardData: () => {},
  isLoadingDashboardData: false,
};

const UserContext = createContext<IUserContext>(initialData);

export const UserProvider = ({ children }: { children: React.ReactNode }) => {
  const [isLoading, setLoading] = useState(false);
  const [userData, setUserData] = useState<IUserContext["userData"]>(null);
  const [isGuest] = useState(false);
  const [isFormCompleted, setFormCompleted] = useState(false);
  const { isLoggedIn } = useGlobal();
  const { pathname, isCurrentPage, eventId } = useEventRouting();
  const {
    data: dashboardData,
    mutate: updateDashboardData,
    refetch: reFetchDashboardData,
    isLoading: isLoadingDashboardData,
  } = useFetcher(() => getTrips(eventId), {
    defaultState: initialData.dashboardData,
    startOnMount: isLoggedIn && !!eventId,
    refetchOnConditions: [isLoggedIn, pathname],
    enabled:
      isCurrentPage(ROUTES.dashboard.base, { exact: false }) && !!eventId,
  });

  const [, setProfiles, { current: profiles }] = useStateRef<{
    [key: string]: any;
  }>({});

  const getUserProfile = async (
    id: string | number,
    role: "user" | "companion",
    force = false,
  ) => {
    const profile = profiles[id];
    if (profile && !force) {
      return {
        ...profile,
        stale: true,
      };
    }

    const { ok, data } = await getProfile(id, role, eventId);
    if (ok) {
      const profile = {
        ...data,
        lastFetched: new Date().getTime(),
      };

      setProfiles((prev) => ({
        ...prev,
        [id]: profile,
      }));

      return profile;
    }

    return null;
  };

  const updateProfile = (id: string | number, data: any) => {
    if (!id || !data) return;

    setProfiles((prev) => ({
      ...prev,
      [id]: {
        ...prev[id],
        ...data,
      },
    }));
  };

  const ctx: IUserContext = {
    isLoading,
    setLoading,
    userData,
    setUserData,
    isGuest,
    isFormCompleted,
    setFormCompleted,
    dashboardData: dashboardData ?? initialData.dashboardData,
    setDashboardData: (newDashboardData) => {
      if (typeof newDashboardData === "function") {
        updateDashboardData(newDashboardData(dashboardData));
      } else {
        updateDashboardData(newDashboardData);
      }
    },
    getUserProfile,
    updateProfile,
    reFetchDashboardData,
    isLoadingDashboardData,
  };

  return <UserContext.Provider value={ctx}>{children}</UserContext.Provider>;
};

export const useUser = () => useContext(UserContext);
